// frontend/src/pages/admin/leads.jsx
// Updated: March 26, 2025
// Purpose: Admin interface for managing leads

import React, { useState, useEffect, useCallback } from 'react';
import { getAdminLeads, synchronizeUnbounceLeads, synchronizeCYCLeads, createLead, updateLead, deleteLead } from 'api';
import LeadTable from '../../components/LeadTable';
import LeadForm from '../../components/LeadForm';

const LeadsAdmin = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [formMode, setFormMode] = useState('create');
  const [syncingUnbounce, setSyncingUnbounce] = useState(false);
  const [syncingCYC, setSyncingCYC] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const fetchLeads = useCallback(async (force = false) => {
    // Don't fetch if already loading unless forced
    if (loading && !force) return;
    
    setLoading(true);
    try {
      console.log("Fetching admin leads...");
      const result = await getAdminLeads();
      console.log("API response:", result);
      
      if (result.success) {
        let leadsData = [];
        
        // Handle different response formats
        if (result.leads) {
          leadsData = result.leads;
        } else if (result.data) {
          leadsData = result.data;
        }
        
        // Make sure we're setting an array, even if empty
        setLeads(Array.isArray(leadsData) ? leadsData : []);
      } else {
        setError(result.message || 'Failed to load leads');
      }
    } catch (err) {
      console.error('API error:', err);
      setError('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    fetchLeads(true); // Force initial fetch
  }, [fetchLeads]); // Add fetchLeads to dependency array

  const handleCreateLead = () => {
    setCurrentLead(null);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEditLead = (lead) => {
    setCurrentLead(lead);
    setFormMode('edit');
    setShowForm(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const response = await deleteLead(leadId);
      if (response.success) {
        setLeads(leads.filter(lead => lead.id !== leadId));
        setSuccess('Lead deleted successfully');
      } else {
        setError(response.message || 'Failed to delete lead');
      }
    } catch (err) {
      console.error('Error deleting lead:', err);
      setError('Failed to delete lead. Please try again.');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setError('');
      setSuccess('');
      
      const response = formMode === 'create' 
        ? await createLead(formData) 
        : await updateLead(currentLead.id, formData);
      
      if (response.success) {
        setShowForm(false);
        await fetchLeads();
        setSuccess(formMode === 'create' ? 'Lead created successfully' : 'Lead updated successfully');
      } else {
        setError(response.message || `Failed to ${formMode} lead`);
      }
    } catch (err) {
      console.error(`Error ${formMode}ing lead:`, err);
      setError(`Failed to ${formMode} lead. Please try again.`);
    }
  };

  const handleSyncUnbounce = async () => {
    try {
      setError('');
      setSuccess('');
      setSyncingUnbounce(true);
      
      console.log('Starting Unbounce sync...');
      const response = await synchronizeUnbounceLeads();
      console.log('Sync response:', response);
      
      if (response.success) {
        setSuccess('Unbounce leads synchronized successfully. ' + (response.message || ''));
        fetchLeads(true); // Force fetch after sync
      } else {
        setError(response.message || 'Failed to synchronize Unbounce leads');
      }
    } catch (err) {
      console.error('Error synchronizing Unbounce leads:', err);
      setError('Failed to synchronize Unbounce leads. Please try again.');
    } finally {
      setSyncingUnbounce(false);
    }
  };

  const handleSyncCYC = async () => {
    try {
      setError('');
      setSuccess('');
      setSyncingCYC(true);
      
      console.log('Starting CYC sync...');
      const response = await synchronizeCYCLeads();
      console.log('CYC sync response:', response);
      
      if (response.success) {
        setSuccess('CYC leads synchronized successfully. ' + (response.message || ''));
        fetchLeads(true); // Force fetch after sync
      } else {
        setError(response.message || 'Failed to synchronize CYC leads');
      }
    } catch (err) {
      console.error('Error synchronizing CYC leads:', err);
      setError('Failed to synchronize CYC leads. Please try again.');
    } finally {
      setSyncingCYC(false);
    }
  };

  const handleSelectLead = (leadId, isSelected) => {
    setSelectedLeads(prev => {
      if (isSelected) {
        return [...prev, leadId];
      } else {
        return prev.filter(id => id !== leadId);
      }
    });
  };

  const handleBulkDelete = async (leadIds) => {
    if (!window.confirm(`Are you sure you want to delete ${leadIds.length} leads?`)) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete leads one by one
      for (const leadId of leadIds) {
        const response = await deleteLead(leadId);
        if (!response.success) {
          throw new Error(`Failed to delete lead ${leadId}`);
        }
      }
      
      // Update the leads list
      setLeads(leads.filter(lead => !leadIds.includes(lead.id)));
      setSelectedLeads([]); // Clear selections
      setSuccess(`Successfully deleted ${leadIds.length} leads`);
    } catch (err) {
      console.error('Error deleting leads:', err);
      setError('Failed to delete some leads. Please try again.');
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between mb-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Manage Leads</h1>
        <div className="flex gap-2">
          <button 
            onClick={handleSyncUnbounce}
            disabled={syncingUnbounce}
            className={`btn ${syncingUnbounce ? 'btn-secondary' : 'btn-primary'} text-sm sm:text-base`}
          >
            {syncingUnbounce ? 'Syncing Unbounce...' : 'Sync from Unbounce'}
          </button>
          <button 
            onClick={handleSyncCYC}
            disabled={syncingCYC}
            className={`btn ${syncingCYC ? 'btn-secondary' : 'btn-primary'} text-sm sm:text-base`}
          >
            {syncingCYC ? 'Syncing CYC...' : 'Sync from CYC'}
          </button>
          <button 
            onClick={handleCreateLead} 
            className="btn btn-primary text-sm sm:text-base"
          >
            Add New Lead
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 mb-4">
          {success}
        </div>
      )}
      
      {showForm && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          <h2 className="text-lg font-medium mb-4">
            {formMode === 'create' ? 'Create New Lead' : 'Edit Lead'}
          </h2>
          <LeadForm
            lead={currentLead}
            onSubmit={handleFormSubmit}
            submitLabel={formMode === 'create' ? 'Create Lead' : 'Update Lead'}
          />
          <button 
            onClick={() => setShowForm(false)} 
            className="btn btn-secondary mt-2"
          >
            Cancel
          </button>
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-500">Loading leads...</p>
        </div>
      ) : (
        <>
          {leads.length === 0 ? (
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try syncing from Unbounce or manually adding a new lead
              </p>
            </div>
          ) : (
            <LeadTable 
              leads={leads} 
              onEdit={handleEditLead} 
              onDelete={handleDeleteLead}
              onBulkDelete={handleBulkDelete}
              selectedLeads={selectedLeads}
              onSelectLead={handleSelectLead}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LeadsAdmin;